import type { LucideIcon } from 'lucide-react';
import { useUserProfile } from '@/features/auth/api';
import { usePendingPromptList } from '@/features/voice-library/api';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { WIDGETS, type WidgetId } from '../config';
import { useDashboardMain } from '../api';

interface KpiCardProps {
  id: WidgetId;
  value: React.ReactNode;
  hint?: React.ReactNode;
  loading?: boolean;
  tone?: 'neutral' | 'positive' | 'warning';
}

/**
 * Compact KPI tile body — the WidgetCard chrome already shows the title
 * in its header, so we only render the icon + value + hint here. Matches
 * the design-system tile in DesignWidgets.tsx so legacy and new KPIs
 * share the same visual language.
 */
function KpiCard({ id, value, hint, loading, tone = 'neutral' }: KpiCardProps) {
  const desc = WIDGETS.find((w) => w.id === id);
  if (!desc) return null;
  const Icon = desc.icon as LucideIcon;

  return (
    <div className="flex h-full items-center gap-2">
      <span
        className={cn(
          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg',
          tone === 'positive' &&
            'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400',
          tone === 'warning' &&
            'bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400',
          tone === 'neutral' && 'bg-primary/10 text-primary',
        )}
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <span className="truncate text-[10px] text-muted-foreground">
            {desc.title}
          </span>
          {hint && (
            <span className="truncate text-[9px] text-muted-foreground/80">
              {hint}
            </span>
          )}
        </div>
        {loading ? (
          <Skeleton className="mt-1 h-5 w-20" />
        ) : (
          <div className="text-lg font-semibold tabular-nums leading-tight text-foreground">
            {value}
          </div>
        )}
      </div>
    </div>
  );
}

const fmt = (n: number | undefined) =>
  typeof n === 'number' ? n.toLocaleString() : '—';

export function VoiceCreditsKpi() {
  const profile = useUserProfile();
  const credits = Number(
    (profile.data as { credits?: string | number } | undefined)?.credits ?? 0,
  );
  return (
    <KpiCard
      id="kpi.voice-credits"
      loading={profile.isLoading}
      value={fmt(credits)}
      hint={profile.data?.planName ? `Plan · ${profile.data.planName}` : 'Voice'}
    />
  );
}

export function TtsCreditsKpi() {
  const profile = useUserProfile();
  const credits = Number(
    (profile.data as { ttsCredits?: string | number } | undefined)
      ?.ttsCredits ?? 0,
  );
  return (
    <KpiCard
      id="kpi.tts-credits"
      loading={profile.isLoading}
      value={fmt(credits)}
      hint="Characters remaining"
    />
  );
}

export function CallsTodayKpi() {
  const dash = useDashboardMain();
  const h = dash.data?.dbHeader;
  const total = h?.totalCalls;
  const connected = h?.connectedCalls;
  const rate =
    total && total > 0 && typeof connected === 'number'
      ? Math.round((connected / total) * 100)
      : null;
  return (
    <KpiCard
      id="kpi.calls-today"
      loading={dash.isLoading}
      value={fmt(total)}
      hint={
        rate != null ? (
          <>
            <span className="text-emerald-700">{rate}%</span> connected
          </>
        ) : (
          'Connected rate'
        )
      }
    />
  );
}

export function ActiveCampaignsKpi() {
  const dash = useDashboardMain();
  const running = dash.data?.runningCampaigns?.length ?? 0;
  return (
    <KpiCard
      id="kpi.active-campaigns"
      loading={dash.isLoading}
      value={fmt(running)}
      hint="Right now"
    />
  );
}

export function ResellersKpi() {
  const dash = useDashboardMain();
  return (
    <KpiCard
      id="kpi.resellers"
      loading={dash.isLoading}
      value={fmt(dash.data?.dbHeader?.resellers)}
    />
  );
}

export function EndUsersKpi() {
  const dash = useDashboardMain();
  return (
    <KpiCard
      id="kpi.end-users"
      loading={dash.isLoading}
      value={fmt(dash.data?.dbHeader?.endUsers)}
    />
  );
}

export function PendingPromptsKpi() {
  const pending = usePendingPromptList();
  const count = (pending.data ?? []).filter(
    (p) => Number(p.promptStatus) === 0,
  ).length;
  return (
    <KpiCard
      id="kpi.pending-prompts"
      loading={pending.isLoading}
      value={fmt(count)}
      hint={count > 0 ? 'Awaiting review' : 'All clear'}
      tone={count > 0 ? 'warning' : 'positive'}
    />
  );
}
